// EDEN · the persistent desktop app-shell (a fixed left sidebar, ≥1024px only).
//
// It lives OUTSIDE #app (its own <aside id="eden-nav"> sibling in index.html), so page re-renders
// (app.innerHTML swaps) never touch it. Visual/navigation chrome only — every item points at an
// EXISTING route via data-link (handled by the router's global click listener). Hidden on phones
// and tablets (CSS — they use the bottom tab bar) and whenever signed out / on /signin (no
// body.has-chrome → no rail, no content offset).

import { api } from "./api";
import { esc } from "./dom";
import { me, setMe } from "./state";
import { t } from "./i18n";
import { icon } from "./icons";
import { sectionsFor, isActive, primaryLeaf, type NavLeaf } from "./nav";

function navEl(): HTMLElement | null {
  return document.getElementById("eden-nav");
}

function itemHtml(path: string, n: NavLeaf): string {
  const on = isActive(path, n.href);
  return `<a class="eden-nav-item${on ? " active" : ""}" data-link href="${n.href}"${
    on ? ' aria-current="page"' : ""
  }>${icon(n.icon)}<span>${t(n.key)}</span></a>`;
}

function subItemHtml(path: string, n: NavLeaf): string {
  const on = isActive(path, n.href);
  return `<a class="eden-nav-sub${on ? " active" : ""}" data-link href="${n.href}"${
    on ? ' aria-current="page"' : ""
  }>${icon(n.icon)}<span>${t(n.key)}</span></a>`;
}

/** Initials for the account chip ("Admin (HOD)" → "AH", "Kanha" → "K"). */
export function initials(name: string): string {
  const words = name.replace(/[()]/g, " ").split(/\s+/).filter(Boolean);
  return (words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?");
}

/** Wire the once-only listeners. Call from boot() before the router starts. */
export function initChrome(): void {
  // The Term 1 pilot label: its own element, first in <body> and outside #app and the rail, so no page swap and no
  // sign-in state removes it. It shows on every screen, /signin included; the stylesheet keeps it off paper.
  if (!document.getElementById("eden-pilot")) {
    const label = document.createElement("div");
    label.id = "eden-pilot";
    label.setAttribute("role", "note");
    label.textContent = t("pilot.label");
    document.body.insertBefore(label, document.body.firstChild);
  }
  // The navigation progress bar (router.ts drives it): a thin line at the very top that moves the
  // moment a link is clicked, so a click never looks like it did nothing while a page loads.
  if (!document.getElementById("eden-progress")) {
    const bar = document.createElement("div");
    bar.id = "eden-progress";
    bar.setAttribute("aria-hidden", "true");
    document.body.appendChild(bar);
  }
  // Document-level + call-time navigation import, so there's no router↔chrome import cycle and the
  // listener survives every #eden-nav innerHTML swap.
  document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action="signout"]');
    if (!btn) return;
    e.preventDefault();
    void (async () => {
      const {navigate}=await import('./router');
      navigate('/signin',async()=>{
        try { await api("/api/auth/signout", { method: "POST" }); }
        catch { /* sign out locally even if the network call fails */ }
        setMe(null);
      });
    })();
  });
}

/** Paint the sidebar for the current user + route. Called from the router on every navigation. */
export function renderChrome(path: string): void {
  const nav = navEl();
  if (!nav) return;
  const user = me();
  const show = !!user && path !== "/signin";
  document.body.classList.toggle("has-chrome", show);
  if (!show || !user) {
    nav.innerHTML = "";
    return;
  }

  // The primary CTA (Plan a lesson) is lifted out of its section and rendered filled at the top.
  const primary = primaryLeaf();
  const cta = primary
    ? `<a class="eden-nav-cta${isActive(path, primary.href) ? " active" : ""}" data-link href="${
        primary.href
      }">${icon(primary.icon)}<span>${t(primary.key)}</span></a>`
    : "";

  const sections = sectionsFor(user.auth_role);
  const main = sections
    .filter((s) => !s.secondary)
    .map((s) => {
      const items = s.items.filter((i) => !i.primary).map((i) => itemHtml(path, i)).join("");
      if (!items) return "";
      const head = s.role === "lead" ? `<div class="eden-nav-group lead">${t("nav.lead_group")}</div>` : "";
      return `${head}<div class="eden-nav-list">${items}</div>`;
    })
    .join("");
  const secondary = sections
    .filter((s) => s.secondary)
    .flatMap((s) => s.items)
    .map((i) => subItemHtml(path, i))
    .join("");

  const roleLabel = t(user.auth_role === "lead" ? "nav.role_lead" : "nav.role_teacher");

  nav.innerHTML = `
    <a class="wordmark" data-link href="/" aria-label="EDEN home">Eden<i class="cross" aria-hidden="true"></i></a>
    ${cta}
    <nav class="eden-nav-main" aria-label="Main">${main}</nav>
    <div class="eden-nav-foot">
      <div class="eden-nav-subs">${secondary}</div>
      <div class="eden-user">
        <span class="avatar" aria-hidden="true">${esc(initials(user.name))}</span>
        <span class="who"><span class="name">${esc(user.name)}</span><span class="role">${roleLabel}</span></span>
        <button type="button" class="signout" data-action="signout" title="${esc(t("common.sign_out"))}">${icon("logout")}<span class="sr-only">${t("common.sign_out")}</span></button>
      </div>
    </div>`;
}
